// Phase 123 Plan 06 Task 3 — Browser-side structured-log adapter.
//
// CONTEXT.md §decisions §Structured logging:
//   "Adopt Sentry.logger.info | warn | error across backend, workers,
//    frontend, CLI. Logs are trace-connected and searchable alongside errors
//    in the same Sentry UI. Logger messages keep current shape; adapter
//    layer routes them through the Sentry logger while continuing to stdout
//    for local dev."
//
// Contract:
//   - Route through `Sentry.logger.*` when the Sentry v10 Structured Logs API
//     is available (set by initSentry() with `enableLogs: true`).
//   - Mirror to console.{info|warn|error} in dev (import.meta.env.DEV) so
//     messages stay visible during local development.
//   - No-op gracefully if Sentry isn't initialised (DSN missing, local dev).
//
// Scope rule (per CONTEXT.md §Structured logging):
//   DOMAIN-event sites migrate to this logger. Middleware / setup / debug
//   noise stays on raw `console.*` (see 123-06-PLAN §action step 3 decision
//   table).
import * as Sentry from '@sentry/react';

type LogLevel = 'info' | 'warn' | 'error';

function emit(
  level: LogLevel,
  msg: string,
  attrs?: Record<string, unknown>,
): void {
  // Dev mirror — keeps local-dev console visibility unchanged post-migration.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console[level](`[${level}]`, msg, attrs ?? '');
  }

  // Sentry v10 Structured Logs. Type-shape check because the field is new on
  // @sentry/react v10 and a missing DSN makes the SDK skip setting it. Wrapped
  // in try/catch so a logger path never crashes the app.
  try {
    const sentryLogger = (Sentry as {
      logger?: Record<LogLevel, (m: string, a?: Record<string, unknown>) => void>;
    }).logger;
    sentryLogger?.[level]?.(msg, attrs ?? {});
  } catch {
    /* Sentry unavailable or logger API absent — console mirror is the fallback. */
  }
}

export const logger = {
  info: (msg: string, attrs?: Record<string, unknown>) => emit('info', msg, attrs),
  warn: (msg: string, attrs?: Record<string, unknown>) => emit('warn', msg, attrs),
  error: (msg: string, attrs?: Record<string, unknown>) => emit('error', msg, attrs),
};
